import { Skeleton } from '../ui';
import { ChannelCard } from './ChannelCard';
import { ListingCard } from './ListingCard';
import { PlaylistCard } from './PlaylistCard';
import { useGridFocus } from './useGridFocus';
import type { ListingItem, ListingRow } from './model';
import styles from './listing.module.css';

/**
 * A grid of listing rows, with §9's roving focus. Home and Trending render videos through
 * {@link FeedGrid}; Search renders the heterogeneous version, which is the same grid — one `<ul>`, one
 * key handler, one measured column count — dispatching per row kind.
 *
 * The three kinds are **interleaved in the order the API returned them**, not grouped. That is the
 * point: a relevance ranking that puts a channel third is making a claim, and hoisting every channel
 * into a band above the videos would re-sort the one thing the endpoint got right. Same rule as
 * `sort_by=top` on comments — do not re-order what you did not rank.
 *
 * `aria-label` rather than a heading inside: the grid is a list of links, and the page above it
 * already says what this list is.
 */
export function ListingGrid({
  rows,
  label,
}: {
  readonly rows: readonly ListingRow[];
  readonly label: string;
}) {
  const { attachGrid, onKeyDown, tabIndexFor, onFocusIndex } = useGridFocus(rows.length);

  return (
    <ul className={styles.grid} aria-label={label} ref={attachGrid} onKeyDown={onKeyDown}>
      {rows.map((row, index) => {
        const shared = { tabIndex: tabIndexFor(index), onFocus: () => onFocusIndex(index) };
        switch (row.kind) {
          case 'video':
            return <ListingCard key={row.key} item={row.video} {...shared} />;
          case 'channel':
            return <ChannelCard key={row.key} item={row.channel} {...shared} />;
          case 'playlist':
            return <PlaylistCard key={row.key} item={row.playlist} {...shared} />;
        }
      })}
    </ul>
  );
}

/**
 * The video-only grid. Kept as its own entry point because Home and Trending genuinely cannot return
 * anything else — `/api/v1/popular` and `/api/v1/trending` are single-kind endpoints, and making those
 * two screens wrap every row in a discriminated union would be writing branches that can never run.
 */
export function FeedGrid({
  items,
  label,
}: {
  readonly items: readonly ListingItem[];
  readonly label: string;
}) {
  return (
    <ListingGrid
      label={label}
      rows={items.map((video) => ({ kind: 'video', key: `video:${video.videoId}`, video }))}
    />
  );
}

/**
 * The loading state, in the same grid with the same card box: measured at 221 px against the real
 * card's 229 px, the difference being one line of metadata that a thin row does not have. So the
 * cards land where the skeletons were rather than shoving the page around — the shift that does
 * remain comes from a notice appearing *above* the grid, which nothing can predict before the
 * response arrives. `Skeleton` takes its size from the caller for exactly this reason.
 *
 * `aria-hidden` on the whole thing: the *page* announces that it is loading once, and a screen reader
 * reading out twenty empty boxes is worse than silence.
 */
export function FeedGridSkeleton({ cards }: { readonly cards: number }) {
  return (
    <ul className={styles.grid} aria-hidden="true">
      {Array.from({ length: cards }, (_, index) => (
        <li className={styles.skeletonCard} key={index}>
          {/* The 16:9 box, by ratio rather than by height — the same shape `Thumbnail` draws. */}
          <Skeleton width="100%" height="auto" radius="var(--vs-radius-md)" className={styles.skeletonThumb} />
          <div className={styles.skeletonText}>
            <Skeleton text width="92%" />
            <Skeleton text width="58%" />
            <Skeleton text width="44%" />
          </div>
        </li>
      ))}
    </ul>
  );
}
